import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, IconButton, Tab, Tabs, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { Prompt, useParams } from 'react-router';

import { DataAPIContext, UPDATE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import PageMeta from '../components/PageMeta';
import RecordDetails, { RecordDetailsContext } from '../components/RecordDetails';
import RevisionHistoryDialog from '../components/RevisionHistoryDialog';
import TabPanel, { a11yProps } from '../components/TabPanel';
import UploadImageButton from '../components/UploadImageButton';
import UsedByPanel from '../components/UsedByPanel';
import { ContextStore } from '../store/contextStore';
import { ConfirmationDialogText } from './utils';

const getComparableState = (filter) => {
    const a = {
        ...filter,
    };
    delete a._changed;
    return JSON.stringify(a);
};

const useStyles = makeStyles((theme) => ({
    detailsImage: {
        borderRadius: '20px',
        height: '250px',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    editWrapper: {
        width: '100%',
        minWidth: 0,
        height: `calc(${theme.cp.contentHeight} - 5rem)`,
        overflow: 'auto',
        [theme.breakpoints.down('sm')]: {
            marginTop: '-6rem !important',
            zIndex: 100,
            backgroundColor: 'white',
        },
    },
}));

const validateInput = (filter) => {
    const errors = {};
    let canSave = true;
    if (!filter.name || filter.name.length <= 0 || filter.name === '') {
        if (filter._changed) {
            errors.name = { error: true, helperText: 'Filter name is required' };
        }
        canSave = false;
    }
    return { canSave, errors };
};
const FilterEditForm = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm } = React.useContext(ContextStore);
    const classes = useStyles();
    const [filter, setFilter] = React.useState(props.data);
    const [originalState, setOriginalState] = React.useState(getComparableState(props.data));
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const [currentTab, setCurrentTab] = React.useState(0);
    const hasWritePerm = hasPerm('trip_type', 'write')

    if (filter.id !== props.data.id) {
        setFilter(props.data);
    }

    const currentState = getComparableState(filter);
    const isChanged = originalState === currentState;
    const isExistingImage = filter.image && filter.image.p && filter.image.s;
    const [errors, setErrors] = React.useState({});
    const { canSave } = validateInput(filter);

    const onDrop = React.useCallback(
        (acceptedFiles) => {
            console.log('onDrop', acceptedFiles, filter);
            setFilter({ ...filter, image: acceptedFiles[0], upload: true, _changed: true });
        },
        [filter]
    );

    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: onDrop,
        multiple: false,
    });

    const tabPanelProps = (index, h) => {
        return {
            className: classes.tabPanel,
            value: currentTab,
            index: index,
            //dir: theme.direction,
        };
    };

    const handleTabChange = (ev, newTab) => {
        setCurrentTab(newTab);
    }

    const onFilterSave = (ev) => {
        props.setSyncActive && props.setSyncActive(true);
        const imageFile = filter.upload && filter.image instanceof File ? filter.image : null;
        const { upload, _changed, tour_operator, tags, branchio_link, image, ...payload } = filter;
        if (!imageFile && image && typeof image === 'object' && !(image instanceof File)) {
            payload.image = image;
        }
        dataAPI(UPDATE, props.resource, {
            id: filter.id,
            data: payload,
            ...(imageFile ? { files: { image: imageFile } } : {}),
        })
            .then((res) => {
                if (res.statusCode < 400) {
                    enqueueSnackbar('Filter saved', { variant: 'success' });
                    props.onChange && props.onChange(filter);
                    setOriginalState(getComparableState(filter));
                    setFilter({ ...filter, _changed: false });
                } else {
                    enqueueSnackbar('Error saving filter', { variant: 'error' });
                }
            })
            .catch((err) => {
                enqueueSnackbar('Error saving filter', { variant: 'error' });
            })
            .finally(() => {
                props.setSyncActive && props.setSyncActive(false);
            });
    };

    const onChange = (ev) => {
        setFilter({ ...filter, [ev.target.name]: ev.target.value, _changed: true });
        props.setDetailsDirty && props.setDetailsDirty(true);
    };

    const onAccessTagsChange = (ev, atags, reason) => {
        setFilter((prevState) => ({ ...prevState, atag: atags, _changed: true }));
    };

    const onFilterDelete = (ev) => {
        setConfirmationDialogAction('delete_filter');
    };

    const doFilterDelete = (ev) => {
        props.onDelete && props.onDelete(filter);
        props.onClose();
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_filter':
                doFilterDelete();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const handleOnBlur = (ev) => {
        setErrors(validateInput(filter).errors);
    };

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(filter.tour_operator)}>
        <div>
            <PageMeta title={`Edit Filter - ${filter.name}`} />
            <Prompt when={!!filter._changed && hasPerm('trip_type', 'write')} message="Are you sure you want to leave without saving changes?" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <Grid container direction="row" spacing={2}>
                <Grid item xs={12} className={classes.editWrapper}>
                    <Grid container direction="row" sx={{ width: '100%' }}>
                        <Grid item xs={8}>
                            <Typography variant="h6">Filter Details</Typography>
                        </Grid>
                        <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
                            <IconButton aria-label="close-preview" onClick={props.onClose} size="small" sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                        <Grid item xs={12}>
                            <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                                <Tab label="General" {...a11yProps(0)} />
                                {hasPerm('trip_type', 'view_history') && <Tab label="Change history" {...a11yProps(1)} />}
                                {filter.id && <Tab label="Used by" {...a11yProps(2)} />}
                            </Tabs>
                        </Grid>
                        <TabPanel {...tabPanelProps(0)}>
                            <Grid item xs={12} container direction="row" spacing={2}>
                                <Grid item xs={12} container justifyContent="center">
                                    <input {...dz.getInputProps()} />
                                    <UploadImageButton
                                        disabled={!hasWritePerm}
                                        imageAction="below"
                                        onClick={dz.open}
                                        isUpload={!isExistingImage}
                                        image={filter.image}
                                        imageStyle={{ margin: '1rem' }}
                                    // {...imageProp}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box mt={2} ml={0}>
                                        <TextField
                                            disabled={!hasWritePerm}
                                            helperText={`${20 - filter.name.length} characters left`}
                                            required
                                            variant="filled"
                                            size="small"
                                            name="name"
                                            label="Name"
                                            fullWidth
                                            value={filter.name}
                                            onChange={onChange}
                                            inputProps={{
                                                maxLength: 20,
                                            }}
                                            onBlur={handleOnBlur}
                                            {...errors.name}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box mb={4}>
                                        <TextField disabled={!hasWritePerm} variant="filled" size="small" name="description" label="Description" multiline fullWidth value={filter.description} onChange={onChange} />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box mt={2} ml={0}>
                                        <TextField
                                            disabled={!hasWritePerm}
                                            helperText={`${128 - (filter.custom_id?.length || 0)} characters left`}
                                            variant="filled"
                                            size="small"
                                            name="custom_id"
                                            label="External ID"
                                            fullWidth
                                            value={filter.custom_id}
                                            onChange={onChange}
                                            inputProps={{
                                                maxLength: 128,
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <AccessTagsDropdown disabled={!hasWritePerm} value={filter.atag} onChange={onAccessTagsChange} />
                                </Grid>
                            </Grid>
                        </TabPanel>
                        <TabPanel {...tabPanelProps(1)}>
                            <RevisionHistoryDialog open={true} resource={props.resource} id={filter.id} />
                        </TabPanel>
                        {filter.id && currentTab === 2 && (
                            <Grid item xs={12} sx={{ width: '100%', minWidth: 0, overflow: 'auto' }}>
                                <UsedByPanel resource={props.resource} id={filter.id} layout="narrow" />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <FormActionButtons
                        mode="edit"
                        onSubmit={onFilterSave}
                        submitDisabled={!hasWritePerm || props.syncActive || !canSave || isChanged}
                        onCancel={props.onClose}
                        cancelDisabled={props.syncActive}
                        onDelete={onFilterDelete}
                        deleteDisabled={!hasPerm('trip_type', 'delete') || props.syncActive}
                    />
                </Grid>
            </Grid>
        </div>
        </FormLicenseeContext.Provider>
    );
};

const FilterEdit = (props) => {
    const resource = 'trip_type';
    const routeParams = useParams();
    const id = routeParams.id;
    const { hasPerm } = React.useContext(ContextStore);

    if (!hasPerm('trip_type', 'read')) {
        return <AccessDeniedView />
    }
    return (
        <RecordDetails resource={resource} id={id} fullResponse>
            <RecordDetailsContext.Consumer>{(res) => (res.statusCode === 200 ? <FilterEditForm {...props} resource={resource} id={id} data={res.data} /> : <p>No Record details data found</p>)}</RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};
export default FilterEdit;
